import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { isAdsAdmin, isAdvertiser } from '@/lib/jwt';
import type { AuthMode } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export function LoginPage() {
  const { authenticated, isAdvertiser: adv, isAdmin, login, logout } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>('advertiser');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (authenticated) {
    const to = adv ? '/' : isAdmin ? '/admin' : '/';
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={from || to} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const claims = await login(mode, username.trim(), password);
      if (mode === 'advertiser' && !isAdvertiser(claims)) {
        logout();
        toast.error('These credentials are not an advertiser (vendor) account.');
        return;
      }
      if (mode === 'team' && !isAdsAdmin(claims)) {
        logout();
        toast.error('These credentials do not have ads-admin access.');
        return;
      }
      toast.success('Signed in.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-background p-4">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Megaphone className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold">Fasto Ads</h1>
            <p className="text-xs text-muted-foreground">Advertising management portal</p>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
          {(['advertiser', 'team'] as AuthMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'rounded-md py-1.5 text-sm font-medium transition-colors',
                mode === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              {m === 'advertiser' ? 'Advertiser' : 'Fasto Team'}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              placeholder={mode === 'advertiser' ? 'vendor username' : 'staff username'}
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <Button type="submit" className="w-full" size="lg" loading={busy}>
            Sign in
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          {mode === 'advertiser'
            ? 'Use your Fasto vendor credentials to manage your ad campaigns.'
            : 'Fasto team members sign in to review and manage ads.'}
        </p>
      </Card>
    </div>
  );
}
